import axios from 'axios';

// Studio API — Oplixi Creator backend on Vercel
const STUDIO_URL = process.env.REACT_APP_STUDIO_URL || 'https://oplixi-creator.vercel.app';

const studio = axios.create({
  baseURL: STUDIO_URL,
  timeout: 10000,
});

// Map known broken Google bucket URLs → our self-hosted HLS streams.
//
// The Studio records still point at the old commondatastorage URLs (which now
// return 403); this client-side redirect serves playback from our own
// infrastructure without requiring Studio admin access to patch each record.
//
// Content lives in the PRIVATE S3 bucket `oplixi-videos` under
// `streams/oplixi-originals/{id}/index.m3u8` and is served via a CloudFront
// distribution (Origin Access Control) — the bucket itself is not public.
// Set REACT_APP_CDN_BASE to the distribution domain (e.g.
// https://dxxxxxxxx.cloudfront.net or a custom CNAME like https://cdn.oplixi.com).
// See oplixi-cloudfront/ for the one-shot setup script that creates it.
const CDN_BASE = (process.env.REACT_APP_CDN_BASE || '').replace(/\/$/, '');
const HLS_PREFIX = '/streams/oplixi-originals';

const hls = (id) => (CDN_BASE ? `${CDN_BASE}${HLS_PREFIX}/${id}/index.m3u8` : null);

const GOOGLE_BUCKET_TO_HLS = {
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4':
    hls('demo-big-buck-bunny'),
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ElephantsDream.mp4':
    hls('demo-elephants-dream'),
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/Sintel.mp4':
    hls('demo-sintel'),
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/TearsOfSteel.mp4':
    hls('demo-tears-of-steel'),
  'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4':
    hls('demo-spring'),
};

// Returns the CDN HLS URL when REACT_APP_CDN_BASE is configured; otherwise
// leaves the original URL untouched (no silent swap to a known-broken host).
const normalizeVideoUrl = (url) => {
  if (!url) return url;
  return GOOGLE_BUCKET_TO_HLS[url] || url;
};

// Normalize snake_case Studio API fields to also include camelCase
// so components can use either format consistently
const normalizeStudioMovie = (m) => ({
  ...m,
  thumbnailUrl: m.thumbnailUrl || m.thumbnail_url || null,
  backdropUrl: m.backdropUrl || m.backdrop_url || null,
  videoUrl: normalizeVideoUrl(m.videoUrl || m.video_url || null),
  publishedAt: m.publishedAt || m.release_date || null,
  voteAverage: m.voteAverage || m.vote_average || null,
});

// Fetch all Studio movies
export const getPublicMovies = async () => {
  let studioMovies = [];

  try {
    const res = await studio.get('/api/public/movies');
    if (res.data.success && res.data.movies.length > 0) {
      studioMovies = res.data.movies.map(normalizeStudioMovie);
    }
  } catch (err) {
    console.warn('Studio API unavailable:', err.message);
  }

  return { data: { success: true, movies: studioMovies, studioCount: studioMovies.length } };
};

export const getPublicMovie = async (movieId) => {
  const res = await studio.get('/api/public/movies', { params: { id: movieId } });
  if (res.data.success) {
    if (res.data.movie) {
      res.data.movie = normalizeStudioMovie(res.data.movie);
      return res;
    }
    if (res.data.movies) {
      const found = res.data.movies.find(m => String(m.id) === String(movieId));
      if (found) {
        return { data: { success: true, movie: normalizeStudioMovie(found) } };
      }
    }
  }
  return { data: { success: false, movie: null } };
};

export const getFeaturedMovie = async () => {
  try {
    const res = await studio.get('/api/public/movies');
    if (res.data.success && res.data.movies.length > 0) {
      return { data: { success: true, movie: normalizeStudioMovie(res.data.movies[0]) } };
    }
  } catch (err) {
    console.warn('Studio API unavailable:', err.message);
  }
  return { data: { success: false, movie: null } };
};

// Search Studio movies by title
export const searchStudioMovies = async (query) => {
  try {
    const res = await studio.get('/api/public/movies');
    if (res.data.success && res.data.movies.length > 0) {
      const q = query.toLowerCase();
      const filtered = res.data.movies
        .map(normalizeStudioMovie)
        .filter(m => m.title?.toLowerCase().includes(q) || m.description?.toLowerCase().includes(q));
      return filtered;
    }
  } catch (err) {
    console.warn('Studio search unavailable:', err.message);
  }
  return [];
};

// Fetch playback info from Studio.
export const getPlaybackInfo = async (movieId) => {
  const res = await studio.get('/api/public/playback', { params: { movieId } });
  return res.data;
};

// Playback tracking — connects to Studio
export class PlaybackTracker {
  constructor(movieId, userId) {
    this.movieId = movieId;
    this.userId = userId;
    this.sessionToken = null;
    this.heartbeatInterval = null;
  }

  async start() {
    try {
      let token = sessionStorage.getItem('oplixi_session_token');
      if (!token) {
        const array = new Uint8Array(16);
        crypto.getRandomValues(array);
        token = `sess_${Array.from(array, b => b.toString(16).padStart(2, '0')).join('')}`;
      }
      sessionStorage.setItem('oplixi_session_token', token);

      const res = await studio.post('/api/tracking/play-start', {
        movieId: this.movieId,
        viewerId: this.userId || null,
        sessionToken: token,
      });
      this.sessionToken = res.data.sessionToken;

      this.heartbeatInterval = setInterval(() => {
        this._sendHeartbeat();
      }, 30000);

      return this.sessionToken;
    } catch (err) {
      console.warn('Playback tracking unavailable:', err.message);
      return null;
    }
  }

  async _sendHeartbeat() {
    if (!this.sessionToken) return;
    try {
      await studio.post('/api/tracking/heartbeat', {
        movieId: this.movieId,
        sessionToken: this.sessionToken,
        watchedSeconds: 30,
      });
    } catch (err) {
      console.warn('Heartbeat failed:', err.message);
    }
  }

  async stop() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (!this.sessionToken) return;
    try {
      await studio.post('/api/tracking/play-end', {
        movieId: this.movieId,
        sessionToken: this.sessionToken,
      });
    } catch (err) {
      console.warn('Play end tracking failed:', err.message);
    }
    this.sessionToken = null;
  }
}

export default studio;
